import { Button, showToast } from '@my/ui';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  CircleCheck,
  CircleX,
  ExternalLink,
  Loader2,
  MapPin,
  MapPinOff,
  TriangleAlert,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { geocodeDraft, googleMapsLink } from '../lib/geocode';
import { isCommittable, validateAll, type RowIssues } from '../lib/validateImportRows';
import type { ImportDraft } from '../lib/types';

interface PreviewStepProps {
  drafts: ImportDraft[];
  excludedRefs: Set<string>;
  onDraftChange: (rowRef: string, patch: Partial<ImportDraft>) => void;
  onToggleExclude: (rowRef: string) => void;
  onSetAllExcluded: (excluded: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
}

const PAGE_SIZE = 50;

/**
 * Step 3 — editable preview grid. Every cell is editable inline; each row gets
 * a validation badge (errors block import, warnings are advisory). The
 * operator can exclude rows, fix issues in place, and see live ready/blocked
 * counts before committing. (Per-row geocode lands in the next chunk.)
 */
export function PreviewStep({
  drafts,
  excludedRefs,
  onDraftChange,
  onToggleExclude,
  onSetAllExcluded,
  onBack,
  onContinue,
}: PreviewStepProps) {
  const [page, setPage] = useState(0);

  // Google geocoder — the Maps SDK is loaded app-wide via <APIProvider>;
  // `useMapsLibrary('geocoding')` resolves the lib lazily (null until ready,
  // and stays null when no Maps key is configured).
  const geocodingLib = useMapsLibrary('geocoding');
  const geocoder = useMemo(
    () => (geocodingLib ? new geocodingLib.Geocoder() : null),
    [geocodingLib],
  );
  const [geoProgress, setGeoProgress] = useState<{ done: number; total: number } | null>(null);
  const geocoding = geoProgress !== null;

  const issuesByRef = useMemo(() => validateAll(drafts), [drafts]);

  const counts = useMemo(() => {
    let ready = 0;
    let blocked = 0;
    let excluded = 0;
    for (const d of drafts) {
      if (excludedRefs.has(d.rowRef)) {
        excluded += 1;
        continue;
      }
      if (isCommittable(issuesByRef.get(d.rowRef))) ready += 1;
      else blocked += 1;
    }
    return { ready, blocked, excluded, total: drafts.length };
  }, [drafts, excludedRefs, issuesByRef]);

  const pageCount = Math.max(1, Math.ceil(drafts.length / PAGE_SIZE));
  const pageDrafts = drafts.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const allIncluded = drafts.length > 0 && excludedRefs.size === 0;

  // Included rows that still need a place_id — the geocode targets.
  const toGeocode = useMemo(
    () => drafts.filter((d) => !excludedRefs.has(d.rowRef) && !d.placeId),
    [drafts, excludedRefs],
  );

  /**
   * Resolve every included row that lacks a place_id. Runs sequentially —
   * the await naturally paces requests a few per second, well under the
   * Geocoder's client-side limits — and writes each result back as it lands
   * so the grid fills in live. Unresolved rows simply keep an empty placeId.
   */
  const handleResolveAll = async () => {
    if (!geocoder || toGeocode.length === 0) return;
    const targets = toGeocode;
    setGeoProgress({ done: 0, total: targets.length });
    let resolved = 0;
    for (let i = 0; i < targets.length; i++) {
      const draft = targets[i]!;
      try {
        const patch = await geocodeDraft(geocoder, draft);
        if (patch) {
          onDraftChange(draft.rowRef, patch);
          if (patch.placeId) resolved += 1;
        }
      } catch {
        // non-fatal — row stays unresolved
      }
      setGeoProgress({ done: i + 1, total: targets.length });
    }
    setGeoProgress(null);
    showToast({
      type: resolved === targets.length ? 'success' : 'info',
      message: `Resolved ${resolved} of ${targets.length} location${targets.length === 1 ? '' : 's'}.`,
    });
  };

  return (
    <div className="space-y-3">
      {/* Counts + bulk include toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
            <CircleCheck className="h-3.5 w-3.5" /> {counts.ready} ready
          </span>
          {counts.blocked > 0 && (
            <span className="inline-flex items-center gap-1 font-medium text-red-600">
              <CircleX className="h-3.5 w-3.5" /> {counts.blocked} blocked
            </span>
          )}
          {counts.excluded > 0 && (
            <span className="text-slate-400">{counts.excluded} excluded</span>
          )}
          <span className="text-slate-400">· {counts.total} total</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Geocode action. Disabled when no Maps key (geocoder null) or
              nothing left to resolve. Shows live progress while running. */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void handleResolveAll()}
            disabled={!geocoder || geocoding || toGeocode.length === 0}
          >
            {geocoding ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Resolving {geoProgress?.done}/{geoProgress?.total}…
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {geocoder
                  ? `Resolve locations${toGeocode.length ? ` (${toGeocode.length})` : ''}`
                  : 'Maps not configured'}
              </span>
            )}
          </Button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={allIncluded}
              onChange={(e) => onSetAllExcluded(!e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300"
            />
            Include all
          </label>
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-[60rem] w-full border-collapse text-xs">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <Th className="w-8" />
              <Th className="w-8" />
              <Th>Code</Th>
              <Th>Name *</Th>
              <Th>Address *</Th>
              <Th>City</Th>
              <Th>Postcode</Th>
              <Th>Country *</Th>
              <Th>Lat</Th>
              <Th>Lng</Th>
              <Th className="w-12">Geo</Th>
            </tr>
          </thead>
          <tbody>
            {pageDrafts.map((d) => {
              const issues = issuesByRef.get(d.rowRef) ?? { errors: [], warnings: [] };
              const excluded = excludedRefs.has(d.rowRef);
              return (
                <tr
                  key={d.rowRef}
                  className={
                    'border-t border-slate-100 ' +
                    (excluded ? 'opacity-40' : issues.errors.length ? 'bg-red-50/40' : '')
                  }
                >
                  <Td>
                    <input
                      type="checkbox"
                      checked={!excluded}
                      onChange={() => onToggleExclude(d.rowRef)}
                      className="h-3.5 w-3.5 rounded border-slate-300"
                    />
                  </Td>
                  <Td>
                    <IssueBadge issues={issues} />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.code}
                      placeholder="(auto)"
                      onChange={(v) => onDraftChange(d.rowRef, { code: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.name}
                      invalid={!d.name.trim()}
                      onChange={(v) => onDraftChange(d.rowRef, { name: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.fullAddress}
                      invalid={!d.fullAddress.trim()}
                      onChange={(v) => onDraftChange(d.rowRef, { fullAddress: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell value={d.city} onChange={(v) => onDraftChange(d.rowRef, { city: v })} />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.postcode}
                      onChange={(v) => onDraftChange(d.rowRef, { postcode: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.country}
                      invalid={!d.country.trim()}
                      width="w-12"
                      onChange={(v) => onDraftChange(d.rowRef, { country: v.toUpperCase() })}
                    />
                  </Td>
                  <Td>
                    <NumCell
                      value={d.latitude}
                      onChange={(n) => onDraftChange(d.rowRef, { latitude: n })}
                    />
                  </Td>
                  <Td>
                    <NumCell
                      value={d.longitude}
                      onChange={(n) => onDraftChange(d.rowRef, { longitude: n })}
                    />
                  </Td>
                  <Td>
                    <GeoCell draft={d} />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs">
          <Button type="button" variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <span className="text-slate-500">
            Page {page + 1} of {pageCount}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {counts.ready === 0 && (
            <span className="text-xs text-amber-600">No rows are ready to import.</span>
          )}
          <Button type="button" onClick={onContinue} disabled={counts.ready === 0}>
            Continue ({counts.ready})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`whitespace-nowrap px-2 py-1.5 font-semibold ${className}`}>{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-2 py-1 align-middle">{children}</td>;
}

function IssueBadge({ issues }: { issues: RowIssues }) {
  if (issues.errors.length) {
    return (
      <span title={issues.errors.join('\n')}>
        <CircleX className="h-4 w-4 text-red-500" />
      </span>
    );
  }
  if (issues.warnings.length) {
    return (
      <span title={issues.warnings.join('\n')}>
        <TriangleAlert className="h-4 w-4 text-amber-500" />
      </span>
    );
  }
  return (
    <span title="Ready">
      <CircleCheck className="h-4 w-4 text-emerald-500" />
    </span>
  );
}

function EditCell({
  value,
  onChange,
  placeholder,
  invalid,
  width = 'w-full',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  width?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={
        `${width} min-w-[4rem] rounded-sm border bg-white px-1.5 py-0.5 text-xs ` +
        (invalid ? 'border-red-300 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-400') +
        ' focus:outline-none focus:ring-1'
      }
    />
  );
}

/** Geo status: a filled pin (place_id resolved) links out to Google Maps;
 *  a hollow pin means "not yet resolved". */
function GeoCell({ draft }: { draft: ImportDraft }) {
  const link = googleMapsLink(draft);
  if (draft.placeId) {
    return link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        title={`Place resolved — view on map\n${draft.placeId}`}
        className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800"
      >
        <MapPin className="h-3.5 w-3.5" />
        <ExternalLink className="h-2.5 w-2.5" />
      </a>
    ) : (
      <MapPin className="h-3.5 w-3.5 text-blue-600" />
    );
  }
  return (
    <span title="No place resolved yet">
      <MapPinOff className="h-3.5 w-3.5 text-slate-300" />
    </span>
  );
}

function NumCell({ value, onChange }: { value: number | null; onChange: (n: number | null) => void }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const raw = e.target.value.trim();
        if (raw === '') return onChange(null);
        const n = Number(raw);
        onChange(Number.isFinite(n) ? n : null);
      }}
      className="w-20 rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  );
}
